import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function BlogDetailPage() {
    const { slug } = useParams<{ slug: string }>();
    const [blog, setBlog] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const hasFetched = useRef(false);

    const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001';

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;

        const fetchBlog = async () => {
            try {
                const res = await fetch(`${API_BASE}/api/blogs/${slug}`);
                if (res.ok) {
                    setBlog(await res.json());
                } else {
                    setError('Article not found');
                }
            } catch (err) {
                setError('Failed to load article');
            } finally {
                setLoading(false);
            }
        };
        fetchBlog();
        window.scrollTo(0, 0);
    }, [slug, API_BASE]);

    if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><i className="ri-loader-4-line animate-spin text-4xl text-emerald-600"></i></div>;
    
    if (error || !blog) return (
        <div className="min-h-screen flex flex-col bg-slate-50">
            <Navbar />
            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
                    <i className="ri-error-warning-line text-3xl text-red-500"></i>
                </div>
                <h2 className="text-2xl font-bold text-slate-800 mb-2">Oops! {error}</h2>
                <p className="text-slate-500 mb-8 max-w-sm">The article you're looking for might have been moved or deleted.</p>
                <Link to="/blog" className="bg-emerald-700 text-white px-8 py-3 rounded-xl font-bold transition-all hover:bg-emerald-800 shadow-lg shadow-emerald-700/20">
                    Back to Blog
                </Link>
            </div>
            <Footer />
        </div>
    );

    return (
        <div className="min-h-screen flex flex-col bg-white">
            <Navbar />

            {/* Post Header */}
            <header className="pt-32 pb-12 bg-slate-50 border-b border-slate-100">
                <div className="max-w-4xl mx-auto px-4 md:px-6">
                    <div className="flex items-center gap-2 mb-6">
                        <Link to="/blog" className="text-xs font-bold text-emerald-700 uppercase tracking-widest hover:text-emerald-800 transition-colors">Blog</Link>
                        <i className="ri-arrow-right-s-line text-slate-400"></i>
                        <span className="text-xs font-bold text-slate-600 uppercase tracking-widest bg-slate-100 px-3 py-1 rounded-full">{blog.category || 'Article'}</span>
                    </div>
                    
                    <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-8 leading-[1.15]" style={{ fontFamily: "'Inter', sans-serif" }}>
                        {blog.title}
                    </h1>

                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                                <i className="ri-calendar-event-line"></i>
                            </div>
                            <div>
                                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Published On</p>
                                <p className="text-sm font-bold text-slate-800">{new Date(blog.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                            </div>
                        </div>
                        <div className="h-10 w-px bg-slate-200" />
                        <div className="flex items-center gap-6 text-slate-500 text-sm">
                            <span className="flex items-center gap-1.5 font-medium"><i className="ri-time-line text-slate-300"></i> {Math.ceil(blog.content.split(' ').length / 200)} min read</span>
                            <span className="flex items-center gap-1.5 font-medium"><i className="ri-eye-line text-slate-300"></i> {blog.views} views</span>
                        </div>
                    </div>
                </div>
            </header>

            <main className="flex-1 py-12">
                <article className="max-w-4xl mx-auto px-4 md:px-6">
                    {blog.coverImage && (
                        <div className="rounded-[2.5rem] overflow-hidden mb-12 shadow-2xl shadow-slate-200">
                            <img src={blog.coverImage} alt={blog.title} className="w-full h-auto max-h-[500px] object-cover" />
                        </div>
                    )}

                    {blog.excerpt && (
                        <div className="text-xl md:text-2xl text-slate-600 font-medium leading-relaxed mb-12 italic border-l-4 border-emerald-500 pl-8">
                            {blog.excerpt}
                        </div>
                    )}

                    <div className="prose prose-lg max-w-none text-slate-700 leading-loose space-y-6" style={{ whiteSpace: 'pre-wrap' }}>
                        {blog.content}
                    </div>

                    <div className="mt-16 pt-12 border-t border-slate-100 flex flex-wrap gap-2">
                        {blog.tags && blog.tags.split(',').map((tag: string) => (
                            <span key={tag} className="px-5 py-2 rounded-full bg-slate-50 text-slate-500 text-xs font-bold uppercase tracking-widest transition-all hover:bg-emerald-50 hover:text-emerald-700 cursor-default">
                                {tag.trim()}
                            </span>
                        ))}
                    </div>
                </article>
            </main>

            <Footer />
        </div>
    );
}
